import type {
  V2PrivacyAssessment,
  V2PrivacyLevel,
  V2PrivacyReason,
} from "../../../redacted/v2/privacy/types";

/**
 * Where a label sits on the track, from 0 ("easier to trace") to 1 ("harder
 * to trace"). Coarse, fixed positions: a label is not a measurement. The best
 * label fills the track; its words still say it is no guarantee. Unknown sits
 * at 0 (hollow knob, no fill) so it never looks good. The SCSS modifiers in
 * `portfolio/scss/_privacy.scss` use the same values.
 */
export const PRIVACY_POSITION: Readonly<Record<V2PrivacyLevel, number>> = {
  unknown: 0,
  limited: 0.3,
  no_known_link: 1,
};

/** Mixed sits halfway: some parts are easier to trace than others. */
export const PRIVACY_MIXED_POSITION = 0.5;

/** How a meter is drawn: the level, or "mixed" when an asset's notes differ. */
export type PrivacyTone = "unknown" | "limited" | "no-known-link" | "mixed";

/** Everything the UI shows for one asset. */
export type PrivacyView = {
  level: V2PrivacyLevel;
  tone: PrivacyTone;
  /** No assessment yet while one is being computed ("Checking…"). */
  pending: boolean;
  /** A check is running; the previous label stays, only a scan moves. */
  checking: boolean;
  /** The asset's parts differ: fill halfway, faint range to the best. */
  mixed: boolean;
  /** Part of the balance is in the public Spending account (always shown). */
  partlyPublic: boolean;
  /** Separate warning, announced first and never hidden by a label. */
  linkDetected: boolean;
  reasons: readonly V2PrivacyReason[];
};

export const levelTone = (
  level: V2PrivacyLevel
): Exclude<PrivacyTone, "mixed"> =>
  level === "no_known_link" ? "no-known-link" : level;

/** One asset's view. A missing assessment is Unknown, never a good label. */
export const privacyView = (
  assessment: V2PrivacyAssessment | undefined,
  checking: boolean
): PrivacyView => {
  if (!assessment)
    return {
      level: "unknown",
      tone: "unknown",
      pending: checking,
      checking,
      mixed: false,
      partlyPublic: false,
      linkDetected: false,
      reasons: [],
    };
  // Mixed only means something below the best label: fill halfway and show
  // the rest as a faint range.
  const mixed = assessment.mixed && assessment.level === "limited";
  return {
    level: assessment.level,
    tone: mixed ? "mixed" : levelTone(assessment.level),
    pending: false,
    checking,
    mixed,
    partlyPublic: assessment.partlyPublic,
    linkDetected: assessment.linkDetected,
    reasons: assessment.reasons,
  };
};

/** A private asset as the portfolio lists it. */
export type PrivacyAsset = {
  /** Native denom, the key of `V2PrivacyState.byDenom`. */
  denom: string;
  symbol: string;
};

/** Weakest first: a known weakness, then Unknown, then no known link. */
const WEAKNESS: Readonly<Record<V2PrivacyLevel, number>> = {
  limited: 0,
  unknown: 1,
  no_known_link: 2,
};

/** Assets with their views, weakest first (stable within a level). */
export const byWeakness = <T extends { view: PrivacyView }>(
  items: readonly T[]
): T[] =>
  items
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        WEAKNESS[a.item.view.level] - WEAKNESS[b.item.view.level] ||
        a.index - b.index
    )
    .map(({ item }) => item);

/**
 * "2 minutes ago" in the UI language, or undefined under a minute. Only the
 * age of the local check is formatted; no amounts or assets are involved.
 */
export const checkedAgo = (
  elapsedMs: number,
  language: string
): string | undefined => {
  const minutes = Math.floor(Math.max(0, elapsedMs) / 60_000);
  if (minutes < 1) return undefined;
  let format: Intl.RelativeTimeFormat;
  try {
    format = new Intl.RelativeTimeFormat(language, { numeric: "always" });
  } catch {
    format = new Intl.RelativeTimeFormat("en", { numeric: "always" });
  }
  if (minutes < 60) return format.format(-minutes, "minute");
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return format.format(-hours, "hour");
  return format.format(-Math.floor(hours / 24), "day");
};
